// useProductSelection.ts
import { useState, useEffect, useCallback } from 'react';
import logger, { Logger } from '../utils/logger';

// Product type definitions (from original component)
interface Product {
  quantity: number;
  _id: string;
  name: string;
  description?: string;
  price: number;
  category?: {
    _id: string;
    name: string;
  };
  extraDetails?: {
    unit?: string;
    [key: string]: any;
  };
  images?: Array<{
    filePath: string;
    fileName: string;
  }>;
}

interface ProductWithQuantity extends Product {
  id?: string;
  quantity: number; // Can be decimal
}

interface UseProductSelectionProps {
  isOpen: boolean;
  initialSelectedProducts?: ProductWithQuantity[];
  onProductsSelect: (products: ProductWithQuantity[]) => void;
}

export const useProductSelection = ({
  isOpen,
  initialSelectedProducts = [],
  onProductsSelect
}: UseProductSelectionProps) => {
  const [selectedProducts, setSelectedProducts] = useState<Map<string, ProductWithQuantity>>(new Map());

  // Initialize selected products when popup opens
  useEffect(() => {
    if (!isOpen) return;

    logger.log('🔄 useProductSelection: Initializing selected products');
    logger.log('🔍 Initial products count:', initialSelectedProducts?.length);
    
    const initialProductsMap = new Map<string, ProductWithQuantity>();
    
    if (initialSelectedProducts?.length > 0) {
      initialSelectedProducts.forEach((product, index) => {
        const productId = product._id || product.id;
        
        if (!productId) {
          console.error(`❌ Product ${index} missing ID:`, product);
          return;
        }
        
        initialProductsMap.set(productId, {
          ...product,
          _id: productId,
          quantity: product.quantity || 1
        });
        
        logger.log(`✅ Added product ${productId} to selection`);
      });
    }
    
    logger.log('📊 Final selection map size:', initialProductsMap.size);
    
    // Only update state if the new map is actually different
    setSelectedProducts(prev => {
      if (prev.size === initialProductsMap.size) {
        let isEqual = true;
        for (const [key, value] of initialProductsMap) {
          const prevValue = prev.get(key);
          if (!prevValue || prevValue.quantity !== value.quantity) {
            isEqual = false;
            break;
          }
        }
        if (isEqual) {
          logger.log('⏭️ Skipping state update - maps are identical');
          return prev;
        }
      }
      return initialProductsMap;
    });
  }, [isOpen]); // Only depend on isOpen - the initialSelectedProducts are handled by the comparison above

  // Toggle product selection
  const handleToggleProduct = useCallback((product: Product) => {
    Logger.log('🔄 Toggling product:', product.name);
    
    setSelectedProducts(prevSelected => {
      const newMap = new Map(prevSelected);
      
      if (newMap.has(product._id)) {
        logger.log('➖ Removing product:', product._id);
        newMap.delete(product._id);
      } else {
        logger.log('➕ Adding product:', product._id);
        newMap.set(product._id, { ...product, quantity: 1 });
      }
      
      return newMap;
    });
  }, []);

  // Update product quantity
  const handleQuantityChange = useCallback((productId: string, changeOrValue: number) => {
    Logger.log('🔄 Changing quantity for:', productId, 'Change:', changeOrValue);
    
    if (!productId || isNaN(changeOrValue)) {
      console.warn('⚠️ Invalid quantity update:', { productId, changeOrValue });
      return;
    }
    
    setSelectedProducts(prevSelected => {
      const newMap = new Map(prevSelected);
      const product = newMap.get(productId);
      
      if (product) {
        let newQuantity;
        
        // If changeOrValue is between -10 and 10, treat as an increment
        if (changeOrValue >= -10 && changeOrValue <= 10 && changeOrValue !== 0) {
          newQuantity = product.quantity + changeOrValue;
        } else {
          // Otherwise treat as absolute value
          newQuantity = changeOrValue;
        }
        
        // Ensure minimum quantity and fix decimal precision
        const validQuantity = Math.max(1, parseFloat(newQuantity.toFixed(2)));
        
        logger.log('📊 Quantity update:', {
          product: product.name,
          old: product.quantity,
          new: validQuantity
        });
        
        newMap.set(productId, { ...product, quantity: validQuantity });
      }
      
      return newMap;
    });
  }, []);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    let itemCount = 0;
    let subtotal = 0;
    
    selectedProducts.forEach(product => {
      itemCount += product.quantity;
      subtotal += product.price * product.quantity;
    });
    
    logger.log('💰 Calculated totals:', { itemCount, subtotal });
    return { itemCount, subtotal };
  }, [selectedProducts]);

  // Handle confirmation
  const handleConfirm = useCallback(() => {
    const productsArray = Array.from(selectedProducts.values());
    logger.log('✅ Confirming selection:', productsArray.length, 'products');
    onProductsSelect(productsArray);
  }, [selectedProducts, onProductsSelect]);

  return {
    selectedProducts,
    handleToggleProduct,
    handleQuantityChange,
    calculateTotals,
    handleConfirm
  };
};